// Every document list the app uses. They live in the backend only
// (../uno-backend/helper/tenantDocumentLists.js); ./loadTenantDocumentLists.js
// loads them once per page load from GET /config/tenant-document-lists.
//
// - docsMap: document name aliases (raw name -> name used in the lists)
// - forensic_docs_map: landlord document view and reports, per tenant type
// - dea_missing_docs: the same, for workspaces with DEA rules on
// - checklist_docs: what the tenant is asked to upload, with the label
//   translation key of each row (was src/Utils/docsData.js)
// - profession_docs: what the landlord sees as missing (was
//   constants.PROFESSION_DOCS)
// - version: short fingerprint of the lists, for the logs
//
// How to read them:
// - React components: useTenantDocumentLists(), inside TenantDocumentListsGate
//   (or withTenantDocumentLists), which waits until they are loaded.
// - Plain functions called from those components: getTenantDocumentLists().
// - Async code that may run before that: first
//   `await loadTenantDocumentLists()`.
//
// This file has no network code, so plain helpers (and their tests) can
// import it without loading axios.

const LIST_NAMES = [
  "docsMap",
  "forensic_docs_map",
  "dea_missing_docs",
  "checklist_docs",
  "profession_docs",
];

let lists = null;
const listeners = new Set();

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isTextMap = (value) =>
  isPlainObject(value) &&
  Object.values(value).every((entry) => typeof entry === "string");

// Only accept an answer that has every list in the shape the app reads
export const looksLikeTenantDocumentLists = (data) =>
  isPlainObject(data) &&
  LIST_NAMES.every(
    (name) => isPlainObject(data[name]) && Object.keys(data[name]).length > 0
  ) &&
  isTextMap(data.docsMap) &&
  ["forensic_docs_map", "dea_missing_docs"].every((name) =>
    Object.values(data[name]).every(isPlainObject)
  ) &&
  Object.entries(data.checklist_docs).every(([bucket, entries]) =>
    bucket === "CLAVE_DOCS"
      ? Array.isArray(entries) &&
        entries.every((entry) => typeof entry === "string")
      : isTextMap(entries)
  ) &&
  Object.values(data.profession_docs).every(isTextMap);

// Saves the lists (called by loadTenantDocumentLists) and tells subscribers
export const setTenantDocumentLists = (data) => {
  if (!looksLikeTenantDocumentLists(data)) {
    throw new Error("[tenantDocumentLists] unexpected answer");
  }
  lists = {
    version: data.version ?? "unknown",
    docsMap: data.docsMap,
    forensic_docs_map: data.forensic_docs_map,
    dea_missing_docs: data.dea_missing_docs,
    checklist_docs: data.checklist_docs,
    profession_docs: data.profession_docs,
  };
  listeners.forEach((listener) => listener(lists));
  return lists;
};

// The loaded lists. Throws when they are not loaded yet, so a page never works
// with empty lists by mistake (that would show no missing documents).
export const getTenantDocumentLists = () => {
  if (!lists) {
    throw new Error(
      "[tenantDocumentLists] not loaded yet: await loadTenantDocumentLists() or render inside TenantDocumentListsGate"
    );
  }
  return lists;
};

export const getLoadedTenantDocumentListsOrNull = () => lists;

// Called with the lists once they load (whoever started the request)
export const subscribeToTenantDocumentLists = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
